import { Button as MantineButton } from '@mantine/core'
import { PDF_MIME_TYPE } from '@mantine/dropzone'
import { UseFormReturnType } from '@mantine/form'

import DnUpload from 'src/components/Inputs/DnUpload/DnUpload'
import TurnstileWidget from 'src/components/Turnstile/TurnstileWidget'
import { UseTurnstileTokenResult } from 'src/components/Turnstile/useTurnstileToken'
import { CustomQuestionFieldType } from 'src/graphql/types/customQuestions'

import ApplicationMultiSelectInput from '../DriverApplicationPage/components/ApplicationMultiSelectInput'
import ApplicationNumberInput from '../DriverApplicationPage/components/ApplicationNumberInput'
import ApplicationPhoneInput from '../DriverApplicationPage/components/ApplicationPhoneInput'
import ApplicationRadioQuestion from '../DriverApplicationPage/components/ApplicationRadioQuestion'
import ApplicationSelectInput from '../DriverApplicationPage/components/ApplicationSelectInput'
import ApplicationTextareaInput from '../DriverApplicationPage/components/ApplicationTextareaInput'
import ApplicationTextInput from '../DriverApplicationPage/components/ApplicationTextInput'
import TermsAndPrivacyCheckbox from '../DriverApplicationPage/components/TermsAndPrivacyCheckbox'
import TextMessageConsentCheckbox from '../DriverApplicationPage/components/TextMessageConsentCheckbox'

import {
  PublicTemplateQuestion,
  QuestionAnswerValue,
} from './shortFormQuestions.utils'
import { ShortFormValues } from './useShortFormSubmit'

// The form's value shape lives with the submit hook that owns the schema and
// `useForm` wiring; re-exported here so callers keep a single import site.
export type { ShortFormValues }

interface ShortFormCardProps {
  form: UseFormReturnType<ShortFormValues>
  isOfficeJob: boolean
  resumeFile: File[]
  setResumeFile: (files: File[]) => void
  companyId?: string
  companyName?: string
  loading: boolean
  onSubmit: () => void
  fullWidthSubmit?: boolean
  /** Hide the inline submit button when an external Apply bar submits the form */
  showSubmit?: boolean
  /** Short-form template questions, already sorted by template drag order */
  publicQuestions?: PublicTemplateQuestion[]
  /** From useShortFormSubmit: renders the Cloudflare Turnstile widget above
   *  the submit controls. Renders nothing when no site key is configured. */
  turnstile?: UseTurnstileTokenResult
}

// 12px gray under the label — matches the mockup's help-text style
const HELP_TEXT_PROPS = {
  className: 'text-xs text-doubleNickel-gray-500',
}

const ShortFormQuestionField = ({
  question,
  value,
  error,
  onChange,
}: {
  question: PublicTemplateQuestion
  value: QuestionAnswerValue
  error?: React.ReactNode
  onChange: (value: QuestionAnswerValue) => void
}) => {
  const { label, fieldType, isRequired, placeholder, helpText, options } =
    question

  const formattedOptions = [...(options ?? [])]
    .sort((a, b) => a.order - b.order)
    .map((option) => ({
      value: option.customQuestionOptionId,
      label: option.label,
    }))

  switch (fieldType) {
    case CustomQuestionFieldType.SHORT_TEXT:
      return (
        <ApplicationTextInput
          label={label}
          placeholder={placeholder || 'Enter your answer'}
          description={helpText || undefined}
          descriptionProps={HELP_TEXT_PROPS}
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          required={isRequired}
          error={error}
        />
      )

    case CustomQuestionFieldType.LONG_TEXT:
      return (
        <ApplicationTextareaInput
          label={label}
          placeholder={placeholder || 'Enter your answer'}
          description={helpText || undefined}
          descriptionProps={HELP_TEXT_PROPS}
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          required={isRequired}
          error={error}
        />
      )

    case CustomQuestionFieldType.BOOLEAN:
      return (
        <ApplicationRadioQuestion
          question={label}
          value={value}
          handleChange={(val: string) => onChange(val)}
          description={helpText || undefined}
          descriptionProps={HELP_TEXT_PROPS}
          required={isRequired}
          error={error}
        />
      )

    case CustomQuestionFieldType.SINGLE_SELECT:
      return (
        <ApplicationSelectInput
          label={label}
          description={helpText || undefined}
          descriptionProps={HELP_TEXT_PROPS}
          value={(value as string) || ''}
          onChange={(event) => onChange(event.target.value)}
          data={[
            {
              value: '',
              label: placeholder || 'Select an option',
              disabled: true,
            },
            ...formattedOptions,
          ]}
          required={isRequired}
          error={error}
        />
      )

    case CustomQuestionFieldType.MULTI_SELECT:
      return (
        <ApplicationMultiSelectInput
          label={label}
          placeholder={placeholder || 'Select options'}
          description={helpText || undefined}
          descriptionProps={HELP_TEXT_PROPS}
          value={(value as string[]) || []}
          onChange={(val: string[]) => onChange(val)}
          data={formattedOptions}
          required={isRequired}
          error={error}
        />
      )

    default:
      return null
  }
}

const ShortFormCard = ({
  form,
  isOfficeJob,
  resumeFile,
  setResumeFile,
  companyId,
  companyName,
  loading,
  onSubmit,
  fullWidthSubmit = false,
  showSubmit = true,
  publicQuestions = [],
  turnstile,
}: ShortFormCardProps) => {
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      {/* DOM order matches the visual order on both breakpoints so keyboard
          focus follows the layout: first/middle/last, phone/email, and
          zip/experience rows on desktop. */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-6 sm:gap-y-5">
        <div className="sm:col-span-2">
          <ApplicationTextInput
            label="First Name"
            placeholder="First name"
            required
            {...form.getInputProps('firstName')}
          />
        </div>
        <div className="sm:col-span-2">
          <ApplicationTextInput
            label="Middle Name"
            placeholder="Middle name"
            {...form.getInputProps('middleName')}
          />
        </div>
        <div className="col-span-2 sm:col-span-2">
          <ApplicationTextInput
            label="Last Name"
            placeholder="Last name"
            required
            {...form.getInputProps('lastName')}
          />
        </div>
        <div className="col-span-2 sm:col-span-3">
          <ApplicationPhoneInput
            label="Phone Number"
            required
            phone={form.values.phone}
            error={form.errors.phone}
            handleChange={(value) => {
              form.setFieldValue('phone', value)
            }}
          />
        </div>
        <div className="col-span-2 sm:col-span-3">
          <ApplicationTextInput
            label="Email Address"
            placeholder="Email address"
            required
            {...form.getInputProps('email')}
          />
        </div>
        <div className="sm:col-span-3">
          <ApplicationTextInput
            label="Postal Code"
            placeholder="Postal code"
            required
            {...form.getInputProps('zipCode')}
          />
        </div>
        <div className="sm:col-span-3">
          <ApplicationNumberInput
            label="Experience"
            decimalScale={1}
            decimalSeparator="."
            step={0.5}
            placeholder="Experience in years"
            required
            {...form.getInputProps('yearsOfExperience')}
          />
        </div>
      </div>
      {isOfficeJob && (
        <div className="flex flex-col gap-3">
          <div className="text-sm font-medium text-doubleNickel-gray-700">
            Resume <span className="text-red-500">*</span>
          </div>
          <DnUpload
            files={resumeFile}
            onDrop={(files) => setResumeFile(files)}
            onRemove={() => setResumeFile([])}
            maxSizeMB={5}
            maxFiles={1}
            accept={PDF_MIME_TYPE}
            uploadText="Click to upload"
            fileTypesText="PDF only, 5 MB maximum"
            dragText="Drag and drop files or browse computer"
          />
        </div>
      )}
      {/* Custom short-form questions — inline continuation of the fixed
          fields (no heading, no divider), one full-width field per question,
          in the template's drag order. */}
      {publicQuestions.map((question) => {
        const fieldKey = `questionAnswers.${question.jobListingTemplateQuestionId}`
        return (
          <div
            key={question.jobListingTemplateQuestionId}
            data-question-id={question.jobListingTemplateQuestionId}
            data-error={!!form.errors[fieldKey]}
          >
            <ShortFormQuestionField
              question={question}
              value={
                form.values.questionAnswers?.[
                  question.jobListingTemplateQuestionId
                ]
              }
              error={form.errors[fieldKey]}
              onChange={(value) => form.setFieldValue(fieldKey, value)}
            />
          </div>
        )
      })}
      <div className="flex flex-col gap-2 text-xs text-gray-600">
        <TermsAndPrivacyCheckbox
          {...form.getInputProps('termsAccepted', { type: 'checkbox' })}
        />
        <div className="rounded-lg border border-doubleNickel-gray-200 p-4">
          <div className="mb-2 text-sm font-medium text-doubleNickel-gray-700">
            Optional Text Message Consent
          </div>
          <TextMessageConsentCheckbox
            companyName={companyName}
            companyId={companyId}
            {...form.getInputProps('isCommunicationOptIn', {
              type: 'checkbox',
            })}
          />
        </div>
      </div>
      {turnstile && (
        <>
          <TurnstileWidget
            ref={turnstile.widgetRef}
            {...turnstile.widgetProps}
            className="empty:hidden"
          />
          {turnstile.verifying && (
            <div
              role="status"
              className="text-center text-xs text-doubleNickel-gray-500"
            >
              Verifying your browser…
            </div>
          )}
        </>
      )}
      {showSubmit && (
        <div
          className={`flex flex-row ${fullWidthSubmit ? '' : 'justify-end'}`}
        >
          <MantineButton
            type="button"
            classNames={{
              root: 'bg-doubleNickel-brand-500 rounded-lg h-11',
            }}
            loading={loading}
            className={fullWidthSubmit ? 'flex-1' : ''}
            onClick={onSubmit}
          >
            Submit
          </MantineButton>
        </div>
      )}
    </form>
  )
}

export default ShortFormCard
